/**
 * AiSuggestions — список подсказок-вопросов для пустого чата.
 *
 *  Группирует по `group`, рендерит лейбл группы + кнопки полной ширины.
 *  При клике вызывает onSelect(text) — выше отправляется в чат.
 */

import { useMemo } from 'react';
import { Icon } from '@/shared/ui';
import { useAiSuggestions, type AiSuggestion } from '@/hooks/api/useAiSuggestions';

export interface AiSuggestionsProps {
  onSelect: (text: string) => void;
}

const FALLBACK: AiSuggestion[] = [
  { group: 'Аналитика', text: 'Покажи CPL за 10 дней кросс-счётчиково по кампаниям.' },
  { group: 'Аналитика', text: 'Какие кампании — лидеры по CPL, какие сворачивать?' },
  { group: 'Мониторинг', text: 'Проверь аномалии: расход без MQL и сломанные цели.' },
  { group: 'Семантика', text: 'Собери спрос по кластеру A через Wordstat.' },
  { group: 'Оптимизация', text: 'Предложи перераспределение бюджета при CPL ≤ 15к.' },
  { group: 'CRM', text: 'Сверь формы Директа со сделками Bitrix за 10 дней.' },
];

export function AiSuggestions({ onSelect }: AiSuggestionsProps) {
  const { data, isLoading, isError } = useAiSuggestions();

  const grouped = useMemo(() => {
    const source = !isError && data && data.length > 0 ? data : FALLBACK;
    const map = new Map<string, AiSuggestion[]>();
    for (const s of source) {
      const key = s.group || 'Подсказки';
      const arr = map.get(key) ?? [];
      arr.push(s);
      map.set(key, arr);
    }
    return Array.from(map.entries());
  }, [data, isError]);

  if (isLoading) {
    return (
      <div className="col gap12">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              height: 42,
              borderRadius: 'var(--r-sm)',
              background:
                'linear-gradient(90deg, var(--surface-alt) 0%, var(--surface-hover) 50%, var(--surface-alt) 100%)',
              backgroundSize: '360px 100%',
              animation: 'attShimmer 1.1s ease-in-out infinite',
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="col gap24">
      {grouped.map(([label, items]) => (
        <div key={label} className="col gap8">
          <div className="eyebrow">{label}</div>
          <div className="col gap6">
            {items.map((s) => (
              <SuggestionRow key={s.text} text={s.text} onClick={() => onSelect(s.text)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SuggestionRow({ text, onClick }: { text: string; onClick: () => void }) {
  return (
    <button className="ai-sug-row" onClick={onClick}>
      <span style={{ flex: 1 }}>{text}</span>
      <span className="ai-sug-arrow" aria-hidden>
        <Icon name="ArrowRight" size={16} />
      </span>
    </button>
  );
}
